import { Helmet } from "react-helmet-async"
import { TrendingUp, Users, Building2, Stethoscope } from "lucide-react"
import { Badge } from "../../components/ui/badge"
import { useState, useEffect } from "react";
import SEO from "../SEO"

export default function Achievements() {
const achievementsSchema = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://pranaamhospitals.com/#organization",
    "name": "Pranaam Hospitals",
    "url": "https://pranaamhospitals.com/",
    "logo": "https://pranaamhospitals.com/images/pranaam-logo.png",
    "description": "Explore the achievements and milestones of Pranaam Hospitals, including NABH accreditation, 50,000+ successful deliveries, multiple locations, and a commitment to excellence in healthcare.",
    "sameAs": [
      "https://www.facebook.com/pranaamhospitals",
      "https://www.instagram.com/pranaamhospitals",
      
    ]
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": "https://pranaamhospitals.com/achievements#breadcrumb",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://pranaamhospitals.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Achievements",
        "item": "https://pranaamhospitals.com/achievements"
      }
    ]
  }
];

const [seo, setSeo] = useState({});
  const [pageKeywords, setPageKeywords] = useState("");
const [pageData, setPageData] = useState(null);
useEffect(() => {
  fetch("/wp-json/custom/v1/page-seo?slug=achievements")
  .then(res => res.json())
  .then(data => { if (data?.seo) setSeo(data.seo); setPageKeywords(data?.keywords || ""); })
  .catch(() => {});
}, []);


  const milestones = [
    { year: "2024", event: "Opened 4th facility in Zaheerabad", icon: Building2 },
    { year: "2023", event: "Achieved NABH Accreditation", icon: TrendingUp },
    { year: "2022", event: "Completed 50,000+ successful deliveries", icon: Users },
    { year: "2021", event: "Expanded to Isnapur with advanced ICU", icon: Stethoscope },
    { year: "2020", event: "Launched 24/7 Emergency Services", icon: TrendingUp },
    { year: "2019", event: "Opened Nallagandla Branch", icon: Building2 }
  ]

  const stats = [
    { number: "50,000+", label: "Patients Treated Annually" },
    { number: "4", label: "Hospital Locations" },
    { number: "100+", label: "Medical Professionals" },
    { number: "20+", label: "Specialties" }
  ]

  return (
    <>
     <SEO
  title={seo.title || "Achievements | Pranaam Hospitals"}
  description={seo.description || "Explore the achievements of Pranaam Hospitals, showcasing our milestones, healthcare excellence, patient care standards, medical innovations, and commitment to quality healthcare in Hyderabad."}
  canonical={seo.canonical || "https://pranaamhospitals.com/achievements"}
  robots={Array.isArray(seo.robots) ? seo.robots.join(", ") : seo.robots || "index, follow"}
  keywords={pageKeywords}
  ogTitle={seo.og_title}
  ogDescription={seo.og_description}
  ogImage={seo.og_image || seo.featured_image}
  twitterTitle={seo.twitter_title}
  twitterDescription={seo.twitter_description}
  author="Pranaam Hospitals"
  publisher="Pranaam Hospitals"
  schema={achievementsSchema}
/>
      
      <main className="min-h-screen bg-gray-50">
        <section className="relative bg-gradient-to-r from-primary/10 to-primary/5 py-16 md:py-24">
          <div className="container px-4 mx-auto">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-4 px-3 py-1">Recognition & Excellence</Badge>
              <h1 className="mb-6 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
                Achievements & <span className="text-primary">Milestones</span>
              </h1>
              <p className="mb-8 text-lg text-gray-700 max-w-3xl mx-auto">
                Our journey is defined by the milestones we've reached and the lives we've touched through compassionate care and medical excellence.
              </p>
            </div>
          </div>
        </section>

        <div className="container mx-auto px-4 py-8 md:py-16">
          <div className="max-w-6xl mx-auto">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {stats.map((stat, index) => (
                <div key={index} className="bg-white rounded-lg shadow-lg p-6 text-center">
                  <div className="text-3xl md:text-4xl font-bold text-brand-teal mb-2">{stat.number}</div>
                  <div className="text-gray-700">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6 md:p-10">
              <h2 className="text-2xl md:text-3xl font-bold text-brand-teal mb-8 text-center">Our Journey</h2>
              <div className="space-y-6">
                {milestones.map((milestone, index) => {
                  const Icon = milestone.icon
                  return (
                    <div key={index} className="flex gap-4 md:gap-6 items-start">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 md:w-16 md:h-16 bg-brand-teal/10 rounded-full flex items-center justify-center">
                          <Icon className="w-6 h-6 md:w-8 md:h-8 text-brand-teal" />
                        </div>
                      </div>
                      <div className="flex-1 pb-6 border-b border-gray-200 last:border-0">
                        <div className="text-brand-teal font-bold text-lg md:text-xl mb-2">{milestone.year}</div>
                        <p className="text-gray-700 text-base md:text-lg">{milestone.event}</p>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  )
}
